import { z } from "zod/v4"
import { PinnedStoryUserId, UserId, UserIdSchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { Period, PeriodState, PeriodRequest } from "./common"
import { DocumentStatus } from "../common"

export const PinnedStoryUserAudience = z.enum(["all", "followers"])

export type PinnedStoryUserAudience = z.infer<typeof PinnedStoryUserAudience>

export const PinnedStoryUserAudienceOptions = [
  { value: "all" as const, label: "全ユーザー" },
  { value: "followers" as const, label: "フォロワーのみ" },
]

export const PinnedStoryUserDocumentMeta = z.object({
  user_id: UserIdSchema.describe("ピン留め対象ユーザーのID"),
  audience: PinnedStoryUserAudience.describe("対象ユーザー（all=全ユーザー、followers=フォロワーのみ）"),
  priority: z.number().int().min(1).max(99999).describe("優先度（1-99999）"),
  display_period: Period.describe("表示期間"),
  status: DocumentStatus.describe("ピン留めの状態"),
})

export type PinnedStoryUserDocumentMeta = z.infer<typeof PinnedStoryUserDocumentMeta>

export const PinnedStoryUserDocumentBody = z.intersection(
  PinnedStoryUserDocumentMeta,
  z.object({
    display_period_state: PeriodState.describe("表示期間の状態（システム自動計算）"),
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type PinnedStoryUserDocumentBody = z.infer<typeof PinnedStoryUserDocumentBody>

export type PinnedStoryUserDocument = PinnedStoryUserDocumentBody & {
  id: PinnedStoryUserId,
}

export class PinnedStoryUserDocuments {

  static toDocumentData(pinnedStoryUser: PinnedStoryUserDocument): object {
    return {
      ...pinnedStoryUser,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): PinnedStoryUserDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: PinnedStoryUserId(doc.id),
      user_id: UserId(data["user_id"]),
      audience: data["audience"],
      priority: data["priority"],
      display_period: data["display_period"],
      display_period_state: data["display_period_state"],
      status: data["status"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const CreatePinnedStoryUserRequest = z.object({
  userId: UserIdSchema.describe("ピン留め対象ユーザーのID"),
  audience: PinnedStoryUserAudience.describe("対象ユーザー"),
  priority: z.number().int().min(1).max(99999).default(1).describe("優先度（1-99999）"),
  displayPeriod: PeriodRequest.describe("表示期間"),
  status: DocumentStatus.describe("ピン留めの状態"),
})

export type CreatePinnedStoryUserRequest = z.infer<typeof CreatePinnedStoryUserRequest>

export const UpdatePinnedStoryUserRequest = z.object({
  userId: UserIdSchema.optional().describe("ピン留め対象ユーザーのID"),
  audience: PinnedStoryUserAudience.optional().describe("対象ユーザー"),
  priority: z.number().int().min(1).max(99999).optional().describe("優先度（1-99999）"),
  displayPeriod: PeriodRequest.optional().describe("表示期間"),
  status: DocumentStatus.optional().describe("ピン留めの状態"),
})

export type UpdatePinnedStoryUserRequest = z.infer<typeof UpdatePinnedStoryUserRequest>
